import React, { useMemo, useRef, useEffect, useState } from 'react';
import { FixedSizeGrid } from 'react-window';
import { useTheme, sans, mono } from '../../tokens';
import MultiYearGridCell from './MultiYearGridCell';

const YEARS = [2026, 2027, 2028, 2029, 2030, 2031];
const MONTHS_PER_YEAR = 12;
const COLS_PER_YEAR = 13; // 12 months + 1 year total
const TOTAL_COLS = YEARS.length * COLS_PER_YEAR;

const fmtTotal = (n) => {
  if (n == null) return '';
  const sign = n < 0 ? '-' : '';
  const v = Math.abs(n);
  if (v >= 1_000_000) return `${sign}$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${sign}$${(v / 1000).toFixed(0)}k`;
  return `${sign}$${v.toFixed(0)}`;
};

/**
 * For a given dept and year, returns { actualTotal, forecastTotal, varPct }.
 * Past year: sum of 12 months (only the months we have summaries for).
 * Current year: YTD sum from Jan to (currentMonth - 1) inclusive of completed months.
 * Future year: actualTotal=null, forecastTotal=full year.
 */
function computeYearTotal({ dept, year, currentYear, currentMonthIdx, forecast, actuals }) {
  const yStr = String(year);
  const fcDept = forecast?.byYear?.[yStr]?.depts?.[dept];
  const fcMonthly = fcDept?.monthly_total || [];

  let upToMonth;
  if (year < currentYear) upToMonth = 12;
  else if (year === currentYear) upToMonth = currentMonthIdx; // exclusive of current month for YTD-completed
  else upToMonth = 0;

  let actualTotal = null;
  if (upToMonth > 0) {
    let anyLoaded = false;
    let sum = 0;
    for (let m = 1; m <= upToMonth; m++) {
      const ym = `${year}-${String(m).padStart(2, '0')}`;
      const summary = actuals?.byMonth?.[ym];
      const v = summary?.view_a?.[dept];
      if (v != null) {
        sum += v;
        anyLoaded = true;
      }
    }
    // Only set actualTotal if at least one month's actuals have loaded.
    // Otherwise leave null so the cell shows the forecast (or skeleton) instead
    // of a misleading -100% variance.
    if (anyLoaded) actualTotal = sum;
  }

  let forecastTotal;
  if (year !== currentYear) {
    forecastTotal = fcMonthly.slice(0, 12).reduce((s, v) => s + (v || 0), 0);
  } else {
    forecastTotal = fcMonthly.slice(0, upToMonth).reduce((s, v) => s + (v || 0), 0);
  }

  let varPct = null;
  if (actualTotal != null && forecastTotal && forecastTotal !== 0) {
    varPct = ((Math.abs(actualTotal) - Math.abs(forecastTotal)) / Math.abs(forecastTotal)) * 100;
  }
  return { actualTotal, forecastTotal, varPct };
}

function computeGLYearTotal({ dept, glAccount, year, currentYear, currentMonthIdx, forecast, actuals }) {
  const yStr = String(year);
  const fcMonthly = forecast?.byYear?.[yStr]?.depts?.[dept]?.by_gl?.[glAccount]?.monthly || [];

  let upToMonth;
  if (year < currentYear) upToMonth = 12;
  else if (year === currentYear) upToMonth = currentMonthIdx;
  else upToMonth = 0;

  let actualTotal = null;
  if (upToMonth > 0) {
    let anyLoaded = false;
    let sum = 0;
    for (let m = 1; m <= upToMonth; m++) {
      const ym = `${year}-${String(m).padStart(2, '0')}`;
      const v = actuals?.byMonth?.[ym]?.by_dept_gl?.[dept]?.[glAccount];
      if (v != null) {
        sum += v;
        anyLoaded = true;
      }
    }
    if (anyLoaded) actualTotal = sum;
  }

  let forecastTotal;
  if (year !== currentYear) {
    forecastTotal = fcMonthly.slice(0, 12).reduce((s, v) => s + (v || 0), 0);
  } else {
    forecastTotal = fcMonthly.slice(0, upToMonth).reduce((s, v) => s + (v || 0), 0);
  }

  let varPct = null;
  if (actualTotal != null && forecastTotal && forecastTotal !== 0) {
    varPct = ((Math.abs(actualTotal) - Math.abs(forecastTotal)) / Math.abs(forecastTotal)) * 100;
  }
  return { actualTotal, forecastTotal, varPct };
}

const DEPT_COL_W = 180;
const CELL_W = 50;
const HEADER_H = 48; // year + month rows together
const ROW_H = 28;

/**
 * Multi-year horizontal variance grid mirroring the MPD CAD financial plan.
 * Sticky year+month header at top, sticky dept name col on left, virtualized
 * body via react-window. Cells will be wired with forecast + actuals in T11.
 */
export default function MultiYearVarianceGrid({
  depts, forecast, actuals, currentMonth, viewportHeight = 480, viewportWidth = 1100,
}) {
  const t = useTheme();

  const gridRef = useRef(null);

  useEffect(() => {
    if (!gridRef.current || !currentMonth) return;
    const [cy, cm] = currentMonth.split('-').map(Number);
    const yearIdx = YEARS.indexOf(cy);
    if (yearIdx < 0) return;
    const targetCol = yearIdx * COLS_PER_YEAR + (cm - 1);
    // Center the current month: target column position - half viewport width
    const targetX = Math.max(0, targetCol * CELL_W - (viewportWidth - DEPT_COL_W) / 2);
    gridRef.current.scrollTo({ scrollLeft: targetX });
  }, [currentMonth, viewportWidth]);

  const [expandedDepts, setExpandedDepts] = useState(new Set());

  const toggleDept = (dept) => {
    setExpandedDepts((prev) => {
      const next = new Set(prev);
      if (next.has(dept)) next.delete(dept);
      else next.add(dept);
      return next;
    });
  };

  const rows = useMemo(() => {
    const out = [];
    for (const dept of depts) {
      out.push({ kind: 'dept', dept });
      if (expandedDepts.has(dept)) {
        // Collect every GL across all years for this dept, sorted by GL #
        const glSet = new Set();
        const glNames = {};
        for (const yStr of Object.keys(forecast?.byYear || {})) {
          const byGl = forecast.byYear[yStr]?.depts?.[dept]?.by_gl || {};
          for (const [gl, def] of Object.entries(byGl)) {
            glSet.add(gl);
            if (def?.name) glNames[gl] = def.name;
          }
        }
        Array.from(glSet).sort().forEach((gl) => {
          out.push({ kind: 'gl', dept, glAccount: gl, glName: glNames[gl] || '' });
        });
      }
    }
    return out;
  }, [depts, expandedDepts, forecast]);

  const colToYearMonth = (col) => {
    const yearIdx = Math.floor(col / COLS_PER_YEAR);
    const offsetInYear = col % COLS_PER_YEAR;
    const year = YEARS[yearIdx];
    if (offsetInYear === 12) return { year, isTotal: true };
    return { year, month: offsetInYear + 1, isTotal: false };
  };

  const cellAt = ({ columnIndex, rowIndex, style }) => {
    const row = rows[rowIndex];
    if (!row) return <div style={style} />;
    const meta = colToYearMonth(columnIndex);

    if (meta.isTotal) {
      const [curY, curM] = currentMonth.split('-').map(Number);
      const totals = row.kind === 'dept'
        ? computeYearTotal({ dept: row.dept, year: meta.year, currentYear: curY, currentMonthIdx: curM, forecast, actuals })
        : computeGLYearTotal({ dept: row.dept, glAccount: row.glAccount, year: meta.year, currentYear: curY, currentMonthIdx: curM, forecast, actuals });
      const color = totals.varPct == null
        ? t.textMuted
        : totals.varPct > 10 ? '#c00' : totals.varPct < -10 ? '#080' : t.textSecondary;
      return (
        <div style={{ ...style, background: t.bgSurface, fontFamily: mono, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 6px', borderRight: `1px solid ${t.border}`, color }}>
          {totals.varPct == null
            ? (totals.actualTotal != null ? fmtTotal(totals.actualTotal) : fmtTotal(totals.forecastTotal))
            : `${totals.varPct > 0 ? '+' : ''}${totals.varPct.toFixed(0)}%`}
        </div>
      );
    }

    const ym = `${meta.year}-${String(meta.month).padStart(2, '0')}`;
    const yStr = String(meta.year);
    const [curY, curM] = currentMonth.split('-').map(Number);
    const isCurrentMonth = (meta.year === curY && meta.month === curM);
    const isFuture = (meta.year > curY) || (meta.year === curY && meta.month > curM);

    let forecastVal, actualVal;
    if (row.kind === 'dept') {
      forecastVal = forecast?.byYear?.[yStr]?.depts?.[row.dept]?.monthly_total?.[meta.month - 1] ?? null;
      actualVal = actuals?.byMonth?.[ym]?.view_a?.[row.dept] ?? null;
    } else {
      forecastVal = forecast?.byYear?.[yStr]?.depts?.[row.dept]?.by_gl?.[row.glAccount]?.monthly?.[meta.month - 1] ?? null;
      actualVal = actuals?.byMonth?.[ym]?.by_dept_gl?.[row.dept]?.[row.glAccount] ?? null;
    }

    const isLoading = !!actuals?.loading?.[ym];

    return (
      <div style={style}>
        <MultiYearGridCell
          actual={actualVal}
          forecast={forecastVal}
          isCurrentMonth={isCurrentMonth}
          isFuture={isFuture}
          isLoading={isLoading}
        />
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: viewportWidth, fontFamily: sans }}>
      {/* Year + Month sticky header */}
      <div style={{ display: 'flex' }}>
        <div style={{ width: DEPT_COL_W, height: HEADER_H, borderRight: `1px solid ${t.border}`, borderBottom: `1px solid ${t.border}`, background: t.bgSurface, padding: '8px 12px', fontSize: 10, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase' }}>
          Department
        </div>
        <div style={{ overflow: 'hidden', width: viewportWidth - DEPT_COL_W }}>
          <div style={{ width: TOTAL_COLS * CELL_W }}>
            <YearHeaderRow t={t} />
            <MonthHeaderRow t={t} />
          </div>
        </div>
      </div>

      {/* Body: sticky dept col + virtualized cell grid */}
      <div style={{ display: 'flex' }}>
        <RowLabelColumn rows={rows} viewportHeight={viewportHeight} t={t} expandedDepts={expandedDepts} onToggle={toggleDept} />
        <FixedSizeGrid
          ref={gridRef}
          columnCount={TOTAL_COLS}
          columnWidth={CELL_W}
          rowCount={rows.length}
          rowHeight={ROW_H}
          width={viewportWidth - DEPT_COL_W}
          height={viewportHeight}
        >
          {cellAt}
        </FixedSizeGrid>
      </div>
    </div>
  );
}

function YearHeaderRow({ t }) {
  return (
    <div style={{ display: 'flex', height: HEADER_H / 2, background: t.bgSurface }}>
      {YEARS.map((y) => (
        <div key={y} style={{ width: COLS_PER_YEAR * CELL_W, textAlign: 'center', fontWeight: 700, fontSize: 12, color: t.textPrimary, borderRight: `1px solid ${t.border}`, lineHeight: `${HEADER_H / 2}px` }}>
          {y}
        </div>
      ))}
    </div>
  );
}

function MonthHeaderRow({ t }) {
  return (
    <div style={{ display: 'flex', height: HEADER_H / 2, background: t.bgCard, borderBottom: `1px solid ${t.border}` }}>
      {YEARS.flatMap((y) => [
        ...Array.from({ length: MONTHS_PER_YEAR }, (_, i) => (
          <div key={`${y}-${i + 1}`} style={{ width: CELL_W, textAlign: 'center', fontSize: 10, color: t.textMuted, fontFamily: mono, lineHeight: `${HEADER_H / 2}px` }}>
            {i + 1}
          </div>
        )),
        <div key={`${y}-total`} style={{ width: CELL_W, textAlign: 'center', fontSize: 9, fontWeight: 700, color: t.textPrimary, background: t.bgSurface, borderRight: `1px solid ${t.border}`, lineHeight: `${HEADER_H / 2}px` }}>
          Total
        </div>,
      ])}
    </div>
  );
}

function RowLabelColumn({ rows, viewportHeight, t, expandedDepts, onToggle }) {
  return (
    <div style={{ width: DEPT_COL_W, height: viewportHeight, overflow: 'hidden', borderRight: `1px solid ${t.border}`, background: t.bgCard }}>
      {rows.map((row) => {
        if (row.kind === 'dept') {
          const expanded = expandedDepts.has(row.dept);
          return (
            <div key={`d-${row.dept}`} onClick={() => onToggle(row.dept)} style={{ height: ROW_H, padding: '0 12px', display: 'flex', alignItems: 'center', borderBottom: `1px solid ${t.borderLight}`, fontSize: 12, fontWeight: 600, color: t.textPrimary, cursor: 'pointer', userSelect: 'none' }}>
              <span style={{ marginRight: 6, color: t.textMuted, fontSize: 9 }}>{expanded ? '▾' : '▸'}</span>
              {row.dept}
            </div>
          );
        }
        return (
          <div key={`g-${row.dept}-${row.glAccount}`} style={{ height: ROW_H, padding: '0 12px 0 28px', display: 'flex', alignItems: 'center', borderBottom: `1px solid ${t.borderLight}`, fontSize: 11, color: t.textSecondary, fontFamily: mono, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <span style={{ color: t.textMuted, marginRight: 6 }}>{row.glAccount}</span>
            {row.glName}
          </div>
        );
      })}
    </div>
  );
}

function DeptColumn({ depts, viewportHeight, t }) {
  return (
    <div style={{ width: DEPT_COL_W, height: viewportHeight, overflow: 'hidden', borderRight: `1px solid ${t.border}`, background: t.bgCard }}>
      {depts.map((dept) => (
        <div key={dept} style={{ height: ROW_H, padding: '0 12px', display: 'flex', alignItems: 'center', borderBottom: `1px solid ${t.borderLight}`, fontSize: 12, fontWeight: 500, color: t.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {dept}
        </div>
      ))}
    </div>
  );
}
